class RedirectBanner extends HTMLElement {
    constructor() {
        super();
        this.isShopifyDesignMode = this.hasAttribute('data-shopify-design-mode');

        console.log('shopify design mode:', this.isShopifyDesignMode);

        if (this.isShopifyDesignMode) {
            this.initialize();
            return;
        }

        this.dateClosed = localStorage.getItem('redirectBannerClosedAt');
        const numberOfMinutesToHide = 120; // Change this value to adjust how long the banner stays hidden after being closed
        if (this.dateClosed && (new Date() - new Date(this.dateClosed)) < numberOfMinutesToHide * 60 * 1000) {
            this.style.display = 'none';
            return;
        }

        this.initialize();
    }

    async initialize() {
        this.template = this.querySelector('template#redirect-banner-template');
        this.config = JSON.parse(this.querySelector('script#redirect-banner-rules').textContent);
        this.rules = this.config.rules;
        this.generalRedirectUrls = Array.isArray(this.config.generalRedirectUrls) ? this.config.generalRedirectUrls : [];
        this.currentUrl = window.location.pathname;
        this.currentRelativeUrl = this.currentUrl.replace(window.Shopify.routes.root.slice(0, -1), '') || '/';
        this.customerCountryData = await this.fetchCustomerCountryData();

        this.evaluateRules();
    }

    async fetchCustomerCountryData() {
        const response = await fetch(`${window.Shopify.routes.root}browsing_context_suggestions.json?country[enabled]=true`);
        const data = await response.json();
        return {
            countryName: data.detected_values.country.name,
            countryHandle: data.detected_values.country.handle,
        }
    }

    evaluateRules() {
        // Do not show banner on blog routes
        if (
            this.currentRelativeUrl.startsWith('/blog') ||
            this.currentRelativeUrl.startsWith('/blogs')
        ) {
            this.style.display = 'none';
            return;
        }

        // Do not show banner if visual editor parameter is present
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('visualEditor') && urlParams.get('visualEditor') === 'convert') {
            this.style.display = 'none';
            return;
        }

        const userCountry = this.customerCountryData.countryHandle.toUpperCase();
        let matchingRule = null;

        // Iterate through all rules to find the first matching one
        for (const rule of this.rules) {

            if (this.isShopifyDesignMode && rule.countries.length === 0) {
                matchingRule = rule;
                break;
            }

            const isInCountryList = rule.countries.includes(userCountry);

            if (isInCountryList) {
                matchingRule = rule;
                break; // Show only the first matching rule
            }
        }

        // Fall back to the US store for countries not covered by any rule
        if (!matchingRule && this.shouldUseFallbackRule(userCountry)) {
            matchingRule = this.config.fallbackRule;
        }

        // Show banner only if a matching rule was found
        if (matchingRule) {
            this.showBanner(matchingRule);
        } else {
            this.style.display = 'none';
        }
    }

    shouldUseFallbackRule(userCountry) {
        const fallbackRule = this.config.fallbackRule;

        if (!fallbackRule || !userCountry) {
            return false;
        }

        const excludedCountries = Array.isArray(fallbackRule.excludedCountries) ? fallbackRule.excludedCountries : [];

        return !excludedCountries.includes(userCountry);
    }

    showBanner(rule) {
        const banner = this.template.content.cloneNode(true).querySelector('.redirect-banner');
        const messageElem = banner.querySelector('.redirect-banner__message');
        const linkElem = banner.querySelector('#redirect-banner-visit-store');
        const closeElem = banner.querySelector('#redirect-banner-close');

        messageElem.innerHTML = this.assembleMessage(rule.message, rule.storeName);

        linkElem.href = this.getRedirectUrl(rule);

        linkElem.textContent = linkElem.textContent.replace('#store_name', rule.storeName);

        closeElem.addEventListener('click', () => {
            banner.classList.add('redirect-banner--closing');
            if (!this.isShopifyDesignMode) {
                localStorage.setItem('redirectBannerClosedAt', new Date().toISOString());
            }
            setTimeout(() => {
                banner.style.display = 'none';
            }, 500);
        });

        banner.style.display = 'flex';
        this.appendChild(banner);
    }

    getRedirectUrl(rule) {
        if (this.shouldRedirectDirectly(rule)) {
            return this.getDirectRedirectUrl(rule);
        }

        return rule.redirectUrl;
    }

    getDirectRedirectUrl(rule) {
        return `${rule.redirectUrl.replace(/\/$/, '')}${this.currentRelativeUrl}`;
    }

    shouldRedirectDirectly(rule) {
        return Boolean(
            rule.redirectDirectly ||
            this.hasDirectRedirectUrl(rule) ||
            this.hasGeneralDirectRedirectUrl()
        );
    }

    hasDirectRedirectUrl(rule) {
        const currentPath = this.normalizePath(this.currentRelativeUrl);
        const directRedirectUrls = Array.isArray(rule.directRedirectUrls) ? rule.directRedirectUrls : [];

        return directRedirectUrls.some(url => this.normalizePath(url) === currentPath);
    }

    hasGeneralDirectRedirectUrl() {
        const currentPath = this.normalizePath(this.currentRelativeUrl);

        return this.generalRedirectUrls.some(url => this.normalizePath(url) === currentPath);
    }

    normalizePath(path) {
        if (!path) {
            return '/';
        }

        const normalizedPath = String(path).trim();

        if (normalizedPath === '/' || normalizedPath === '') {
            return '/';
        }

        return normalizedPath.replace(/\/$/, '');
    }

    assembleMessage(template, storeName) {
        return template.replace('#store_name', storeName).replace('#country', this.customerCountryData.countryName);
    }
}

if (!customElements.get('redirect-banner')) {
    customElements.define('redirect-banner', RedirectBanner);
}